import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, MapPin, Calendar, Package, ArrowRight, Anchor, RefreshCw, ChevronDown, Check, Truck,
    Ship, Clock, Heart, ChevronUp, AlertCircle, Star, TrendingDown, Zap, Loader2
} from 'lucide-react';
import { getTodayForInput, formatDisplayDate } from '../utils/dateUtils';
import { baseURL } from '@/config/APIConfig';

const BACKEND_URL = baseURL.AiBaseURL;

const RateMatrix = () => {
    const [originPort, setOriginPort] = useState('');
    const [destPort, setDestPort] = useState('');
    const [originDisplay, setOriginDisplay] = useState(''); // Display name for origin
    const [destDisplay, setDestDisplay] = useState(''); // Display name for destination
    const [isLookingUpOrigin, setIsLookingUpOrigin] = useState(false);
    const [isLookingUpDest, setIsLookingUpDest] = useState(false);
    const [selectedContainers, setSelectedContainers] = useState(['40STD']);
    const [departureDate, setDepartureDate] = useState(getTodayForInput());
    const [moveType, setMoveType] = useState('port/port');
    const [isSearching, setIsSearching] = useState(false);
    const [isContainerDropdownOpen, setIsContainerDropdownOpen] = useState(false);
    const [isMoveTypeDropdownOpen, setIsMoveTypeDropdownOpen] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [error, setError] = useState(null);
    const [sortBy, setSortBy] = useState('Lowest Price');
    const containerDropdownRef = useRef(null);
    const moveTypeDropdownRef = useRef(null);
    const searchButtonRef = useRef(null);
    const originLookupTimeout = useRef(null);
    const destLookupTimeout = useRef(null);

    // Function to lookup port UNLOCODE using LLM
    const lookupPortUNLOCODE = async (portName, setPort, setDisplay, setLoading) => {
        if (!portName || portName.length < 2) return;

        // If already looks like a valid UNLOCODE (5 uppercase letters), skip lookup
        const trimmed = portName.trim().toUpperCase();
        if (trimmed.length === 5 && /^[A-Z]{5}$/.test(trimmed)) {
            setPort(trimmed);
            setDisplay('');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/port/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ port_name: portName }),
            });

            const data = await response.json();

            if (data.success && data.unlocode && data.unlocode !== 'UNKNOWN') {
                setPort(data.unlocode);
                setDisplay(`${data.full_name}, ${data.country}`);
            }
        } catch (err) {
            console.error('Port lookup error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle origin port input change with debounced lookup
    const handleOriginChange = (value) => {
        setOriginPort(value.toUpperCase());
        setOriginDisplay('');

        // Clear previous timeout
        if (originLookupTimeout.current) {
            clearTimeout(originLookupTimeout.current);
        }

        // Set new timeout for lookup (debounce 800ms)
        if (value.length >= 3 && !/^[A-Z]{5}$/.test(value.toUpperCase())) {
            originLookupTimeout.current = setTimeout(() => {
                lookupPortUNLOCODE(value, setOriginPort, setOriginDisplay, setIsLookingUpOrigin);
            }, 800);
        }
    };

    // Handle destination port input change with debounced lookup
    const handleDestChange = (value) => {
        setDestPort(value.toUpperCase());
        setDestDisplay('');

        // Clear previous timeout
        if (destLookupTimeout.current) {
            clearTimeout(destLookupTimeout.current);
        }

        // Set new timeout for lookup (debounce 800ms)
        if (value.length >= 3 && !/^[A-Z]{5}$/.test(value.toUpperCase())) {
            destLookupTimeout.current = setTimeout(() => {
                lookupPortUNLOCODE(value, setDestPort, setDestDisplay, setIsLookingUpDest);
            }, 800);
        }
    };

    // Handle blur event - trigger lookup immediately if needed
    const handleOriginBlur = () => {
        if (originLookupTimeout.current) {
            clearTimeout(originLookupTimeout.current);
        }
        if (originPort.length >= 3 && !/^[A-Z]{5}$/.test(originPort)) {
            lookupPortUNLOCODE(originPort, setOriginPort, setOriginDisplay, setIsLookingUpOrigin);
        }
    };

    const handleDestBlur = () => {
        if (destLookupTimeout.current) {
            clearTimeout(destLookupTimeout.current);
        }
        if (destPort.length >= 3 && !/^[A-Z]{5}$/.test(destPort)) {
            lookupPortUNLOCODE(destPort, setDestPort, setDestDisplay, setIsLookingUpDest);
        }
    };

    // Listen for voice command events
    useEffect(() => {
        const handleVoiceCommand = (event) => {
            const { origin, destination, auto_search, field, value, action } = event.detail || {};
            console.log('Voice command received:', event.detail);

            // Handle form fill with origin and destination (from simple voice assistant)
            if (origin) {
                console.log('Setting origin to:', origin);
                setOriginPort(origin.toUpperCase());
                setOriginDisplay('');
                lookupPortUNLOCODE(origin, setOriginPort, setOriginDisplay, setIsLookingUpOrigin);
            }

            if (destination) {
                console.log('Setting destination to:', destination);
                setDestPort(destination.toUpperCase());
                setDestDisplay('');
                lookupPortUNLOCODE(destination, setDestPort, setDestDisplay, setIsLookingUpDest);
            }

            // Handle individual field/value pairs (from advanced voice assistant)
            if (field && value) {
                const fieldLower = field.toLowerCase();
                console.log(`Setting field ${fieldLower} to: ${value}`);

                if (fieldLower.includes('origin') || fieldLower.includes('pol') || fieldLower.includes('from')) {
                    setOriginPort(value.toUpperCase());
                    setOriginDisplay('');
                    lookupPortUNLOCODE(value, setOriginPort, setOriginDisplay, setIsLookingUpOrigin);
                } else if (fieldLower.includes('destination') || fieldLower.includes('pod') || fieldLower.includes('to')) {
                    setDestPort(value.toUpperCase());
                    setDestDisplay('');
                    lookupPortUNLOCODE(value, setDestPort, setDestDisplay, setIsLookingUpDest);
                }
            }

            // Handle search action
            if (action === 'search') {
                console.log('Voice triggered search action');
                // Wait a bit for any pending lookups to complete
                setTimeout(() => {
                    if (searchButtonRef.current) {
                        console.log('Auto-triggering search from voice command');
                        searchButtonRef.current.click();
                    }
                }, 2000);
                return;
            }

            // Auto search after form fill (with delay for lookups to complete)
            if (auto_search && origin && destination) {
                setTimeout(() => {
                    if (searchButtonRef.current) {
                        console.log('Auto-triggering search');
                        searchButtonRef.current.click();
                    }
                }, 3000);
            }
        };

        window.addEventListener('voiceFormFill', handleVoiceCommand);
        return () => window.removeEventListener('voiceFormFill', handleVoiceCommand);
    }, []);

    const containerTypes = [
        { value: '20STD', label: "20' Standard", apiName: '20 STD' },
        { value: '40STD', label: "40' Standard", apiName: '40 STD' },
        { value: '40HC', label: "40' High Cube", apiName: '40 HC' },
        { value: '45HC', label: "45' High Cube", apiName: '45 DC' },
        { value: '20RF', label: "20' Reefer", apiName: '20 REF' },
        { value: '40RF', label: "40' Reefer", apiName: '40 REF' },
    ];

    const moveTypes = [
        { value: 'port/port', label: 'port/port' },
        { value: 'port/door', label: 'port/door' },
        { value: 'door/door', label: 'door/door' },
        { value: 'door/port', label: 'door/port' },
    ];

    // Get API container names for selected containers
    const getSelectedApiNames = () => {
        return selectedContainers.map(v => {
            const ct = containerTypes.find(c => c.value === v);
            return ct?.apiName || v;
        });
    };

    // Sort and filter results
    const processedResults = useMemo(() => {
        if (!searchResults || searchResults.length === 0) return [];

        const selectedApiNames = getSelectedApiNames();

        // Calculate total rate (ocean + surcharges) for each result
        const resultsWithRates = searchResults.map(rate => {
            const ptp = rate.PortToPort || {};
            const containerDetails = ptp.ContainerDetails || [];
            const surcharges = ptp.FCLDefineOtherAddtional || [];

            // Get best ocean freight for selected containers
            let bestOceanRate = Infinity;
            selectedApiNames.forEach(apiName => {
                const container = containerDetails.find(c => c.ContainerName === apiName);
                if (container && container.ContainerRate > 0 && container.ContainerRate < bestOceanRate) {
                    bestOceanRate = container.ContainerRate;
                }
            });
            if (bestOceanRate === Infinity) bestOceanRate = 0;

            // Calculate total surcharges (averaged across selected containers)
            let surchargeTotal = 0;
            surcharges.forEach(s => {
                const cds = s.ContainerDetails || [];
                selectedApiNames.forEach(apiName => {
                    const sc = cds.find(c => c.ContainerName === apiName);
                    if (sc && sc.ContainerRate > 0) surchargeTotal += sc.ContainerRate;
                });
            });
            const avgSurcharge = selectedApiNames.length > 0 ? Math.round(surchargeTotal / selectedApiNames.length) : 0;

            const overallTotal = bestOceanRate + avgSurcharge;

            return {
                ...rate,
                calculatedBestRate: bestOceanRate,
                calculatedSurcharges: avgSurcharge,
                calculatedTotal: overallTotal
            };
        });

        // Sort by overall total (ocean + surcharges)
        const sorted = [...resultsWithRates].sort((a, b) => {
            switch (sortBy) {
                case 'Lowest Price':
                    if (a.calculatedTotal === 0 && b.calculatedTotal > 0) return 1;
                    if (b.calculatedTotal === 0 && a.calculatedTotal > 0) return -1;
                    return a.calculatedTotal - b.calculatedTotal;
                case 'Highest Price':
                    return b.calculatedTotal - a.calculatedTotal;
                case 'Transit Time':
                    const ttA = parseInt(a.TransitTime) || 999;
                    const ttB = parseInt(b.TransitTime) || 999;
                    return ttA - ttB;
                default:
                    return 0;
            }
        });

        return sorted;
    }, [searchResults, sortBy, selectedContainers]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerDropdownRef.current && !containerDropdownRef.current.contains(event.target)) {
                setIsContainerDropdownOpen(false);
            }
            if (moveTypeDropdownRef.current && !moveTypeDropdownRef.current.contains(event.target)) {
                setIsMoveTypeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleContainerToggle = (value) => {
        setSelectedContainers(prev => {
            if (prev.includes(value)) {
                if (prev.length === 1) return prev;
                return prev.filter(v => v !== value);
            }
            return [...prev, value];
        });
    };

    const getContainerDisplayText = () => {
        if (selectedContainers.length === 0) return 'Select...';
        if (selectedContainers.length === 1) {
            const container = containerTypes.find(c => c.value === selectedContainers[0]);
            return container?.label || selectedContainers[0];
        }
        return `${selectedContainers.length} selected`;
    };

    const handleSearch = async () => {
        // Auto-trim spaces from port codes
        const trimmedOrigin = originPort.replace(/\s+/g, '').toUpperCase();
        const trimmedDest = destPort.replace(/\s+/g, '').toUpperCase();

        if (!trimmedOrigin || !trimmedDest) {
            setError('Please enter both Origin and Destination port codes');
            return;
        }

        // Update the input fields with trimmed values immediately
        setOriginPort(trimmedOrigin);
        setDestPort(trimmedDest);

        setIsSearching(true);
        setError(null);

        try {
            const response = await fetch(`${BACKEND_URL}/api/rate-matrix/search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pol_code: trimmedOrigin,
                    pod_code: trimmedDest,
                    move_type: moveType,
                    container_types: selectedContainers,
                }),
            });

            const data = await response.json();

            if (data.success) {
                setSearchResults(data.data);
            } else {
                setError(data.detail || 'Failed to fetch rates');
                setSearchResults(null);
            }
        } catch (err) {
            setError('Failed to connect to rate service. Please try again.');
            setSearchResults(null);
        } finally {
            setIsSearching(false);
        }
    };

    const handleClear = () => {
        setOriginPort('');
        setDestPort('');
        setOriginDisplay('');
        setDestDisplay('');
        setSelectedContainers(['20STD']);
        setDepartureDate(getTodayDate());
        setMoveType('port/port');
        setSearchResults(null);
        setError(null);
    };

    const inputClass = "w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs sm:text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all";

    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-3 sm:p-4 lg:p-6 bg-slate-50 dark:bg-slate-950 min-h-screen">
            {/* Header - Responsive */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-5">
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md">
                        <Anchor className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Rate Search</h1>
                        <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">Find the best freight rates</p>
                    </div>
                </div>
                <button onClick={handleClear} className="w-full sm:w-auto px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all flex items-center justify-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5" />
                    Clear All
                </button>
            </div>

            {/* Search Form */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 p-4 sm:p-5 mb-4 sm:mb-6">
                <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                    {/* Origin */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <MapPin className="w-3.5 h-3.5 text-blue-600" />
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Origin (POL)</span>
                            {isLookingUpOrigin && <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />}
                        </div>
                        <input type="text" value={originPort} onChange={(e) => handleOriginChange(e.target.value)} onBlur={handleOriginBlur} placeholder="e.g., New York, USNYC" className={inputClass} />
                        {originDisplay && <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400 truncate">{originDisplay}</p>}
                    </div>

                    {/* Arrow */}
                    <div className="hidden lg:flex items-center justify-center pb-1">
                        <div className="w-8 h-8 bg-blue-50 dark:bg-slate-800 rounded-full flex items-center justify-center border border-slate-200 dark:border-slate-700">
                            <ArrowRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>

                    {/* Destination */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Destination (POD)</span>
                            {isLookingUpDest && <Loader2 className="w-3 h-3 text-emerald-500 animate-spin" />}
                        </div>
                        <input type="text" value={destPort} onChange={(e) => handleDestChange(e.target.value)} onBlur={handleDestBlur} placeholder="e.g., Rotterdam, NLRTM" className={inputClass} />
                        {destDisplay && <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400 truncate">{destDisplay}</p>}
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-9 bg-slate-200 dark:bg-slate-700 mx-1" />

                    {/* Move Type */}
                    <div className="w-full sm:w-36" ref={moveTypeDropdownRef}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <Truck className="w-3.5 h-3.5 text-orange-600" />
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Move Type</span>
                        </div>
                        <div className="relative">
                            <button type="button" onClick={() => setIsMoveTypeDropdownOpen(!isMoveTypeDropdownOpen)} className="w-full px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span>{moveType}</span>
                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isMoveTypeDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {isMoveTypeDropdownOpen && (
                                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
                                        {moveTypes.map((type) => (
                                            <button key={type.value} onClick={() => { setMoveType(type.value); setIsMoveTypeDropdownOpen(false); }} className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${moveType === type.value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                                                <span className="font-medium">{type.label}</span>
                                                {moveType === type.value && <Check className="w-3.5 h-3.5" />}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Container */}
                    <div className="w-full sm:w-40" ref={containerDropdownRef}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <Package className="w-3.5 h-3.5 text-purple-600" />
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Container</span>
                        </div>
                        <div className="relative">
                            <button type="button" onClick={() => setIsContainerDropdownOpen(!isContainerDropdownOpen)} className="w-full px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between">
                                <span className="truncate">{getContainerDisplayText()}</span>
                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ml-1 ${isContainerDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {isContainerDropdownOpen && (
                                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute z-50 mt-1 w-56 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
                                        <div className="p-2">
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400 px-2 font-medium uppercase tracking-wider">Select container types</p>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                            {containerTypes.map((type) => (
                                                <button key={type.value} onClick={() => handleContainerToggle(type.value)} className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center gap-3 ${selectedContainers.includes(type.value) ? 'bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${selectedContainers.includes(type.value) ? 'bg-purple-600 border-purple-600' : 'border-slate-300 dark:border-slate-600'}`}>
                                                        {selectedContainers.includes(type.value) && <Check className="w-2.5 h-2.5 text-white" />}
                                                    </div>
                                                    <span className={`font-medium ${selectedContainers.includes(type.value) ? 'text-purple-700 dark:text-purple-300' : 'text-slate-700 dark:text-slate-300'}`}>{type.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Departure */}
                    <div className="w-full sm:w-36">
                        <div className="flex items-center gap-1.5 mb-1.5">
                            <Calendar className="w-3.5 h-3.5 text-amber-600" />
                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Departure</span>
                        </div>
                        <input type="date" value={departureDate} onChange={(e) => setDepartureDate(e.target.value)} className={`${inputClass} py-2.5`} />
                    </div>

                    {/* Search Button */}
                    <button ref={searchButtonRef} onClick={handleSearch} disabled={isSearching} className="h-10 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all disabled:opacity-50 text-white text-sm font-medium whitespace-nowrap">
                        {isSearching ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Search className="w-4 h-4" /><span>Search</span></>}
                    </button>
                </div>

                {selectedContainers.length > 1 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                        <div className="flex flex-wrap gap-2">
                            <span className="text-xs text-slate-500 dark:text-slate-400 py-1">Selected:</span>
                            {selectedContainers.map(value => {
                                const container = containerTypes.find(c => c.value === value);
                                return (
                                    <span key={value} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                                        {container?.label || value}
                                        <button onClick={() => handleContainerToggle(value)} className="hover:text-purple-900 dark:hover:text-purple-100 ml-1">×</button>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Error */}
            {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
                </motion.div>
            )}

            {/* Results */}
            {processedResults && processedResults.length > 0 && (
                <div className="space-y-4">
                    {/* Results Header */}
                    <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                    <Ship className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                    <span className="text-lg font-bold text-slate-900 dark:text-white">{processedResults.length}</span>
                                    <span className="text-sm text-slate-500 ml-1">rates found</span>
                                </div>
                            </div>
                            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700" />
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                <MapPin className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">{originPort}</span>
                                <ArrowRight className="w-4 h-4" />
                                <span className="font-medium">{destPort}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-500">Sort:</span>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option>Lowest Price</option>
                                <option>Highest Price</option>
                                <option>Transit Time</option>
                            </select>
                        </div>
                    </div>

                    {/* Rate Cards */}
                    <div className="grid gap-4">
                        {processedResults.map((rate, index) => (
                            <RateCard
                                key={index}
                                rate={rate}
                                selectedContainers={selectedContainers}
                                containerTypes={containerTypes}
                                isLowestPrice={index === 0 && sortBy === 'Lowest Price'}
                                resultIndex={index}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* No Results */}
            {searchResults && searchResults.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                    <Ship className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">No rates found</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">Try adjusting your search criteria</p>
                </div>
            )}
        </motion.div>
    );
};

// Booking Success Modal with confetti
const BookingSuccessModal = ({ isOpen, onClose, bookingRef }) => {
    const [particles, setParticles] = useState([]);
    const navigate = useNavigate();
    useEffect(() => {
        if (isOpen) {
            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
            const newParticles = Array.from({ length: 50 }, (_, i) => ({
                id: i,
                x: Math.random() * 100,
                y: -10 - Math.random() * 20,
                color: colors[Math.floor(Math.random() * colors.length)],
                size: 4 + Math.random() * 8,
                delay: Math.random() * 0.5,
                duration: 1.5 + Math.random() * 1.5,
            }));
            setParticles(newParticles);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
            {/* Backdrop */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Confetti */}
            {particles.map(p => (
                <motion.div
                    key={p.id}
                    initial={{ x: `${p.x}vw`, y: `${p.y}vh`, opacity: 1, rotate: 0, scale: 1 }}
                    animate={{ y: '110vh', opacity: 0, rotate: 360 + Math.random() * 360, scale: 0.5 }}
                    transition={{ duration: p.duration, delay: p.delay, ease: 'easeIn' }}
                    className="absolute z-[101] rounded-sm"
                    style={{ width: p.size, height: p.size, backgroundColor: p.color }}
                />
            ))}

            {/* Modal */}
            <motion.div
                initial={{ scale: 0.5, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                className="relative z-[102] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Success Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-200 dark:shadow-emerald-900/30"
                >
                    <Check className="w-10 h-10 text-white" />
                </motion.div>

                <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Booking Request Saved!</motion.h2>

                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="text-slate-500 dark:text-slate-400 text-sm mb-4">Your booking request has been submitted successfully.</motion.p>

                <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 }}
                    className="inline-flex items-center gap-2 px-5 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl mb-6">
                    <Ship className="w-5 h-5 text-blue-600" />
                    <button
                        type="button"
                        onClick={() => navigate(`/booking-request/edit/221`)}
                        className="text-lg font-bold text-blue-700 dark:text-blue-300 cursor-pointer hover:underline bg-transparent border-none p-0"
                    >
                        {bookingRef}
                    </button>
                </motion.div>

                <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                    onClick={onClose}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition-all">
                    Close
                </motion.button>
            </motion.div>
        </div>
    );
};

// Carrier logo URLs
const CARRIER_LOGOS = {
    'YML': 'https://logo.clearbit.com/yangming.com',
    'YANG MING': 'https://logo.clearbit.com/yangming.com',
    'COSCO': 'https://logo.clearbit.com/cosco.com',
    'OOCL': 'https://logo.clearbit.com/oocl.com',
    'MSC': 'https://logo.clearbit.com/msc.com',
    'MAERSK': 'https://logo.clearbit.com/maersk.com',
    'CMA CGM': 'https://logo.clearbit.com/cma-cgm.com',
    'CMA': 'https://logo.clearbit.com/cma-cgm.com',
    'HAPAG-LLOYD': 'https://logo.clearbit.com/hapag-lloyd.com',
    'HAPAG': 'https://logo.clearbit.com/hapag-lloyd.com',
    'ONE': 'https://logo.clearbit.com/one-line.com',
    'EVERGREEN': 'https://logo.clearbit.com/evergreen-marine.com',
    'HMM': 'https://logo.clearbit.com/hmm21.com',
    'ZIM': 'https://logo.clearbit.com/zim.com',
    'PIL': 'https://logo.clearbit.com/pilship.com',
    'WAN HAI': 'https://logo.clearbit.com/wanhai.com',
    'WANHAI': 'https://logo.clearbit.com/wanhai.com',
    'SM LINE': 'https://logo.clearbit.com/smlines.com',
    'SINOTRANS': 'https://logo.clearbit.com/sinotrans.com',
    'SEALAND': 'https://logo.clearbit.com/sealandmaersk.com',
    'MATSON': 'https://logo.clearbit.com/matson.com',
    'SITC': 'https://logo.clearbit.com/sitcline.com',
};

const getCarrierLogo = (name) => {
    if (!name) return null;
    const upper = name.toUpperCase().trim();
    for (const [key, url] of Object.entries(CARRIER_LOGOS)) {
        if (upper.includes(key)) return url;
    }
    return null;
};

// Rate Card Component
const RateCard = ({ rate, selectedContainers, containerTypes, isLowestPrice, resultIndex }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingRef, setBookingRef] = useState('');

    const ptp = rate.PortToPort || {};
    const containerDetails = ptp.ContainerDetails || [];
    const surcharges = ptp.FCLDefineOtherAddtional || [];

    const contractNumber = rate.ContractNumber || 'N/A';
    const carrierName = ptp.CarrierName || 'N/A';
    const carrierLogo = getCarrierLogo(carrierName);
    const isSpotRate = resultIndex < 3;
    const fromPort = ptp.FromPort?.trim() || ptp.FromPortCode || '';
    const toPort = ptp.ToPort?.trim() || ptp.ToPortCode || '';
    const fromPortCode = ptp.FromPortCode || '';
    const toPortCode = ptp.ToPortCode || '';
    const transitTime = rate.TransitTime || ptp.TransitTime || '-';
    const validFrom = rate.ValidFromDate || '';
    const validTo = rate.ValidToDate || '';
    const commodity = ptp.CommDescription || ptp.CommodityType || '';
    const accountName = rate.AccountName || '';

    // Use pre-calculated totals from sorting
    const bestRate = rate.calculatedBestRate || 0;
    const totalSurcharges = rate.calculatedSurcharges || 0;
    const overallTotal = rate.calculatedTotal || 0;

    // Get container API names for selected containers
    const getSelectedApiNames = () => {
        return selectedContainers.map(v => {
            const ct = containerTypes.find(c => c.value === v);
            return ct?.apiName || v;
        });
    };

    // Get rates for selected containers only
    const getFilteredContainerRates = () => {
        const selectedApiNames = getSelectedApiNames();
        return containerDetails.filter(c => selectedApiNames.includes(c.ContainerName));
    };

    const filteredContainers = getFilteredContainerRates();

    const handleBookNow = () => {
        const ref = `OBR04260221`;
        setBookingRef(ref);
        setShowBookingModal(true);
    };

    // Using formatDisplayDate from dateUtils

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-white dark:bg-slate-900 rounded-xl border overflow-hidden transition-shadow hover:shadow-lg ${isLowestPrice ? 'border-green-400 ring-2 ring-green-100 dark:ring-green-900/30' : 'border-slate-200 dark:border-slate-800'
                }`}
        >
            {/* Main Content */}
            <div className="p-5">
                <div className="flex items-start gap-6">
                    {/* Left - Route Info */}
                    <div className="flex-1">
                        {/* Header Row */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center gap-2">
                                {carrierLogo ? (
                                    <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden p-1">
                                        <img src={carrierLogo} alt={carrierName} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-slate-600">${carrierName.slice(0, 3)}</span>`; }} />
                                    </div>
                                ) : (
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                        <Ship className="w-5 h-5 text-white" />
                                    </div>
                                )}
                                <div>
                                    <h3 className="font-bold text-slate-900 dark:text-white">{carrierName}</h3>
                                    {isSpotRate ? (
                                        <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">Spot Rate</p>
                                    ) : (
                                        <p className="text-xs text-slate-500">Contract: {contractNumber}</p>
                                    )}
                                </div>
                            </div>
                            {isLowestPrice && (
                                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full flex items-center gap-1">
                                    <TrendingDown className="w-3 h-3" />
                                    Best Rate
                                </span>
                            )}
                            {transitTime && transitTime !== '-' && (
                                <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-medium rounded-full flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {transitTime} Days
                                </span>
                            )}
                        </div>

                        {/* Route */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">Origin</p>
                                <p className="font-semibold text-slate-900 dark:text-white">{fromPort || fromPortCode}</p>
                                <p className="text-xs text-blue-600">{fromPortCode}</p>
                            </div>
                            <div className="flex flex-col items-center">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full flex items-center justify-center">
                                    <ArrowRight className="w-4 h-4 text-white" />
                                </div>
                            </div>
                            <div className="flex-1 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
                                <p className="text-xs text-slate-500 dark:text-slate-400 uppercase mb-1">Destination</p>
                                <p className="font-semibold text-slate-900 dark:text-white">{toPort || toPortCode}</p>
                                <p className="text-xs text-emerald-600">{toPortCode}</p>
                            </div>
                        </div>

                        {/* Container Rates - show total per container */}
                        <div className="flex flex-wrap gap-2">
                            {filteredContainers.map((container, idx) => (
                                <div
                                    key={idx}
                                    className={`px-3 py-2 rounded-lg border text-sm ${container.ContainerRate > 0
                                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                                        : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                        }`}
                                >
                                    <span className="font-medium text-slate-700 dark:text-slate-300">{container.ContainerName}</span>
                                    <span className={`ml-2 font-bold ${overallTotal > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`}>
                                        ${overallTotal > 0 ? overallTotal.toLocaleString() : '0'}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Validity & Vessel Departure */}
                        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                            <span>Valid: {formatDisplayDate(validFrom)} - {formatDisplayDate(validTo)}</span>
                            <span className="flex items-center gap-1">
                                <Ship className="w-3 h-3" />
                                <span className="font-semibold text-slate-700 dark:text-slate-300">Next Departure:</span> {(() => {
                                    const d = new Date(); d.setDate(d.getDate() + 3 + (resultIndex * 2));
                                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                })()}
                            </span>
                            {commodity && <span>• {commodity.slice(0, 50)}{commodity.length > 50 ? '...' : ''}</span>}
                        </div>
                    </div>

                    {/* Right - Price & Actions */}
                    <div className="w-52 text-right flex flex-col items-end flex-shrink-0">
                        <button onClick={() => setIsFavorite(!isFavorite)} className={`p-1.5 rounded-full transition-colors mb-2 ${isFavorite ? 'text-red-500 bg-red-50' : 'text-slate-300 hover:text-red-400 hover:bg-slate-50'}`}>
                            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                        </button>

                        {/* Total */}
                        <div className="mb-2 w-full">
                            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</p>
                            <p className="text-3xl font-bold text-slate-900 dark:text-white">
                                ${overallTotal > 0 ? overallTotal.toLocaleString() : '0'}
                            </p>
                            <p className="text-[10px] text-slate-400">per container</p>
                        </div>

                        {/* Breakdown */}
                        <div className="w-full space-y-1 mb-3 text-left">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500 dark:text-slate-400">Ocean Freight</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">${bestRate > 0 ? bestRate.toLocaleString() : '0'}</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500 dark:text-slate-400">Additional</span>
                                <span className="font-semibold text-slate-700 dark:text-slate-200">${totalSurcharges > 0 ? totalSurcharges.toLocaleString() : '0'}</span>
                            </div>
                        </div>

                        <button onClick={handleBookNow} className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow transition-all flex items-center justify-center gap-2">
                            <Zap className="w-4 h-4" />
                            Book Now
                        </button>

                        <button onClick={() => setIsExpanded(!isExpanded)} className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            {isExpanded ? 'Hide' : 'Details'}
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Expanded Details */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <div className="px-5 pb-5 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-4">
                            {/* All Container Rates */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                                    <Package className="w-4 h-4 text-purple-500" />
                                    Ocean Freight Rates
                                </h4>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Container</th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Rate (USD)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredContainers.map((c, idx) => (
                                                <tr key={idx} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                                                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{c.ContainerName}</td>
                                                    <td className="px-3 py-2 text-right font-semibold text-slate-900 dark:text-white">${c.ContainerRate || 0}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Surcharges */}
                            {surcharges.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                                        <Star className="w-4 h-4 text-amber-500" />
                                        Additional Charges
                                    </h4>
                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Charge</th>
                                                    {filteredContainers.map((c, idx) => (
                                                        <th key={idx} className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase">{c.ContainerName}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {surcharges.slice(0, 5).map((surcharge, idx) => (
                                                    <tr key={idx} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                                                        <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                                                            <span className="font-medium">{surcharge.SurchargeCode}</span>
                                                            <span className="text-xs text-slate-400 ml-1">({surcharge.Description?.slice(0, 25)}...)</span>
                                                        </td>
                                                        {getSelectedApiNames().map((apiName, cIdx) => {
                                                            const sc = (surcharge.ContainerDetails || []).find(c => c.ContainerName === apiName);
                                                            return (
                                                                <td key={cIdx} className="px-3 py-2 text-center text-slate-600 dark:text-slate-400">${sc?.ContainerRate || 0}</td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Other Info */}
                            {accountName && (
                                <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                                    {accountName}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Booking Success Modal */}
            <BookingSuccessModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} bookingRef={bookingRef} />
        </motion.div>
    );
};

export default RateMatrix;
